/**
 * Variable Browser Component
 *
 * Hierarchical tree view for browsing and selecting output variables.
 * Groups variables by category (Zone, System, Meters) and key value.
 * Supports keyboard navigation and accessibility.
 */

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import type { OutputVariable } from '@/simulation/parsers/types';

/**
 * Tree node structure
 */
interface TreeNode {
  id: string;
  label: string;
  type: 'category' | 'group' | 'variable';
  children?: TreeNode[];
  variable?: OutputVariable;
  expanded?: boolean;
  /** Count of variables in this node (for categories/groups) */
  variableCount?: number;
}

/**
 * Props for VariableBrowser component
 */
interface VariableBrowserProps {
  /** Available output variables */
  variables: OutputVariable[];
  /** Currently selected variable IDs */
  selectedIds?: number[];
  /** Disabled variable IDs (cannot be selected) */
  disabledIds?: Set<number>;
  /** Callback when a variable is selected */
  onSelect?: (variable: OutputVariable) => void;
  /** Callback when selection changes (multi-select mode) */
  onSelectionChange?: (variables: OutputVariable[]) => void;
  /** Enable multi-select mode */
  multiSelect?: boolean;
  /** Maximum height for the browser */
  maxHeight?: number;
  /** Additional CSS classes */
  className?: string;
  /** Warning message to display */
  warningMessage?: string;
}

/**
 * Categorize a variable based on its name
 */
function getVariableCategory(variable: OutputVariable): string {
  const name = variable.variableName.toLowerCase();
  const key = variable.keyValue.toLowerCase();

  // Meter variables
  if (name.includes('meter') || key.includes(':')) {
    return 'Meters';
  }

  // Zone variables
  if (name.includes('zone') || key.includes('zone')) {
    return 'Zone Variables';
  }

  // Surface variables
  if (name.includes('surface') || key.includes('surface')) {
    return 'Surface Variables';
  }

  // System/HVAC variables
  if (
    name.includes('system') ||
    name.includes('coil') ||
    name.includes('fan') ||
    name.includes('pump') ||
    name.includes('chiller') ||
    name.includes('boiler')
  ) {
    return 'System Variables';
  }

  // Environment variables
  if (name.includes('outdoor') || name.includes('site') || name.includes('environment')) {
    return 'Environment Variables';
  }

  return 'Other Variables';
}

/**
 * Count total variables in a tree node
 */
function countVariables(node: TreeNode): number {
  if (node.type === 'variable') return 1;
  return node.children?.reduce((sum, child) => sum + countVariables(child), 0) ?? 0;
}

/**
 * Build hierarchical tree from flat variable list
 */
function buildTree(variables: OutputVariable[]): TreeNode[] {
  // Group by category
  const categoryMap = new Map<string, Map<string, OutputVariable[]>>();

  for (const variable of variables) {
    const category = getVariableCategory(variable);
    let groupMap = categoryMap.get(category);
    if (!groupMap) {
      groupMap = new Map();
      categoryMap.set(category, groupMap);
    }

    const groupKey = variable.keyValue || 'General';
    let group = groupMap.get(groupKey);
    if (!group) {
      group = [];
      groupMap.set(groupKey, group);
    }
    group.push(variable);
  }

  // Build tree structure
  const tree: TreeNode[] = [];
  const categoryOrder = [
    'Zone Variables',
    'System Variables',
    'Meters',
    'Surface Variables',
    'Environment Variables',
    'Other Variables',
  ];

  for (const category of categoryOrder) {
    const groupMap = categoryMap.get(category);
    if (!groupMap || groupMap.size === 0) continue;

    const categoryNode: TreeNode = {
      id: `cat-${category}`,
      label: category,
      type: 'category',
      children: [],
      expanded: false,
      variableCount: 0,
    };

    // Sort groups alphabetically
    const sortedGroups = Array.from(groupMap.entries()).sort((a, b) =>
      a[0].localeCompare(b[0])
    );

    for (const [groupKey, groupVariables] of sortedGroups) {
      // If there's only one variable in the group, add it directly
      if (groupVariables.length === 1) {
        const v = groupVariables[0];
        if (v) {
          categoryNode.children?.push({
            id: `var-${String(v.id)}`,
            label: `${v.variableName}${v.units ? ` [${v.units}]` : ''}`,
            type: 'variable',
            variable: v,
          });
        }
      } else {
        // Create a group node
        const groupNode: TreeNode = {
          id: `group-${category}-${groupKey}`,
          label: groupKey,
          type: 'group',
          children: [],
          expanded: false,
          variableCount: groupVariables.length,
        };

        // Sort variables alphabetically within group
        const sortedVariables = [...groupVariables].sort((a, b) =>
          a.variableName.localeCompare(b.variableName)
        );

        for (const v of sortedVariables) {
          groupNode.children?.push({
            id: `var-${String(v.id)}`,
            label: `${v.variableName}${v.units ? ` [${v.units}]` : ''}`,
            type: 'variable',
            variable: v,
          });
        }

        categoryNode.children?.push(groupNode);
      }
    }

    // Calculate total count for category
    categoryNode.variableCount = countVariables(categoryNode);
    tree.push(categoryNode);
  }

  return tree;
}

/**
 * Icon components
 */
function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
      />
    </svg>
  );
}

function VariableIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
}

/**
 * Tree node component with keyboard support
 */
function TreeNodeItem({
  node,
  level,
  selectedIds,
  disabledIds,
  expandedIds,
  onToggle,
  onSelect,
  focusedId,
  onFocus,
}: {
  node: TreeNode;
  level: number;
  selectedIds: Set<number>;
  disabledIds: Set<number>;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelect: (variable: OutputVariable) => void;
  focusedId: string | null;
  onFocus: (id: string) => void;
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const isExpanded = expandedIds.has(node.id);
  const isSelected = node.variable && selectedIds.has(node.variable.id);
  const isDisabled = node.variable && disabledIds.has(node.variable.id);
  const isFocused = focusedId === node.id;
  const hasChildren = node.children && node.children.length > 0;
  const paddingLeft = level * 16 + 8;

  // Scroll focused item into view
  useEffect(() => {
    if (isFocused && nodeRef.current) {
      nodeRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isFocused]);

  const handleClick = () => {
    onFocus(node.id);
    if (isDisabled) return;
    if (node.type === 'variable' && node.variable) {
      onSelect(node.variable);
    } else if (hasChildren) {
      onToggle(node.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <>
      <div
        ref={nodeRef}
        role={node.type === 'variable' ? 'treeitem' : 'group'}
        aria-selected={isSelected ?? false}
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-disabled={isDisabled ?? false}
        tabIndex={isFocused ? 0 : -1}
        className={`flex items-center py-1.5 px-2 transition-colors outline-none ${
          isDisabled
            ? 'opacity-40 cursor-not-allowed'
            : isSelected
              ? 'bg-blue-600/30 text-blue-300 cursor-pointer'
              : isFocused
                ? 'bg-muted/70 ring-1 ring-blue-500 cursor-pointer'
                : 'text-foreground hover:bg-muted/50 cursor-pointer'
        }`}
        style={{ paddingLeft }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        title={isDisabled ? 'Cannot add more than 2 unit types to a chart' : node.label}
      >
        {/* Expand/collapse indicator */}
        {hasChildren ? (
          <span className="text-muted-foreground mr-1" aria-hidden="true">
            <ChevronIcon expanded={isExpanded} />
          </span>
        ) : (
          <span className="w-5" aria-hidden="true" />
        )}

        {/* Icon */}
        <span className="text-muted-foreground mr-2" aria-hidden="true">
          {node.type === 'variable' ? <VariableIcon /> : <FolderIcon />}
        </span>

        {/* Label */}
        <span className="text-sm truncate flex-1">
          {node.label}
        </span>

        {/* Count badge for categories/groups */}
        {node.variableCount !== undefined && node.variableCount > 0 && (
          <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full ml-2">
            {node.variableCount}
          </span>
        )}

        {/* Variable info */}
        {node.variable && (
          <span className="text-xs text-muted-foreground ml-2">
            {node.variable.reportFrequency}
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && node.children && (
        <div role="group">
          {node.children.map((child) => (
            <TreeNodeItem
              key={child.id}
              node={child}
              level={level + 1}
              selectedIds={selectedIds}
              disabledIds={disabledIds}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onSelect={onSelect}
              focusedId={focusedId}
              onFocus={onFocus}
            />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Collect all node IDs in order for keyboard navigation
 */
function collectNodeIds(nodes: TreeNode[], expandedIds: Set<string>): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    ids.push(node.id);
    if (node.children && expandedIds.has(node.id)) {
      ids.push(...collectNodeIds(node.children, expandedIds));
    }
  }
  return ids;
}

/**
 * Variable Browser Component
 */
export function VariableBrowser({
  variables,
  selectedIds = [],
  disabledIds = new Set<number>(),
  onSelect,
  onSelectionChange,
  multiSelect = false,
  maxHeight = 400,
  className = '',
  warningMessage,
}: VariableBrowserProps) {
  const [filter, setFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync internal selection with props
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Build tree from variables
  const tree = useMemo(() => buildTree(variables), [variables]);

  // Filter variables
  const filteredTree = useMemo(() => {
    if (!filter.trim()) return tree;

    const lowerFilter = filter.toLowerCase();

    function filterNode(node: TreeNode): TreeNode | null {
      if (node.type === 'variable') {
        const matches =
          node.label.toLowerCase().includes(lowerFilter) ||
          node.variable?.keyValue.toLowerCase().includes(lowerFilter);
        return matches ? node : null;
      }

      const filteredChildren = node.children
        ?.map(filterNode)
        .filter((n): n is TreeNode => n !== null);

      if (!filteredChildren || filteredChildren.length === 0) return null;

      // Update count for filtered results
      const count = filteredChildren.reduce((sum, child) =>
        sum + (child.type === 'variable' ? 1 : (child.variableCount ?? 0)), 0);

      return { ...node, children: filteredChildren, expanded: true, variableCount: count };
    }

    return tree.map(filterNode).filter((n): n is TreeNode => n !== null);
  }, [tree, filter]);

  // Auto-expand when filtering
  const effectiveExpandedIds = useMemo(() => {
    if (!filter.trim()) return expandedIds;

    const ids = new Set<string>();
    function collectAllIds(node: TreeNode) {
      if (node.children) {
        ids.add(node.id);
        node.children.forEach(collectAllIds);
      }
    }
    filteredTree.forEach(collectAllIds);
    return ids;
  }, [filteredTree, filter, expandedIds]);

  // Collect visible node IDs for keyboard navigation
  const visibleNodeIds = useMemo(
    () => collectNodeIds(filteredTree, effectiveExpandedIds),
    [filteredTree, effectiveExpandedIds]
  );

  // Find node by ID
  const findNode = useCallback((id: string, nodes: TreeNode[]): TreeNode | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNode(id, node.children);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (!focusedId) return;

      const currentIndex = visibleNodeIds.indexOf(focusedId);
      if (currentIndex === -1) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (currentIndex < visibleNodeIds.length - 1) {
            setFocusedId(visibleNodeIds[currentIndex + 1] ?? null);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (currentIndex > 0) {
            setFocusedId(visibleNodeIds[currentIndex - 1] ?? null);
          }
          break;
        case 'ArrowRight': {
          e.preventDefault();
          const node = findNode(focusedId, filteredTree);
          if (node?.children && !effectiveExpandedIds.has(focusedId)) {
            handleToggle(focusedId);
          } else if (node?.children && node.children.length > 0) {
            setFocusedId(node.children[0]?.id ?? null);
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          const node = findNode(focusedId, filteredTree);
          if (node?.children && effectiveExpandedIds.has(focusedId)) {
            handleToggle(focusedId);
          }
          break;
        }
        case 'Home':
          e.preventDefault();
          if (visibleNodeIds.length > 0) {
            setFocusedId(visibleNodeIds[0] ?? null);
          }
          break;
        case 'End':
          e.preventDefault();
          if (visibleNodeIds.length > 0) {
            setFocusedId(visibleNodeIds[visibleNodeIds.length - 1] ?? null);
          }
          break;
      }
    }

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [focusedId, visibleNodeIds, effectiveExpandedIds, findNode, filteredTree]);

  // Toggle node expansion
  const handleToggle = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Handle variable selection
  const handleSelect = useCallback(
    (variable: OutputVariable) => {
      // Skip if variable is disabled
      if (disabledIds.has(variable.id)) {
        return;
      }

      if (multiSelect) {
        // Toggle selection
        const isCurrentlySelected = selectedIdSet.has(variable.id);
        const newSelectedIds = new Set(selectedIdSet);

        if (isCurrentlySelected) {
          newSelectedIds.delete(variable.id);
        } else {
          newSelectedIds.add(variable.id);
        }

        // Call onSelectionChange with updated selection
        if (onSelectionChange) {
          const selectedVars = variables.filter((v) => newSelectedIds.has(v.id));
          onSelectionChange(selectedVars);
        }

        // Also call onSelect for single variable notification
        if (onSelect) {
          onSelect(variable);
        }
      } else {
        if (onSelect) {
          onSelect(variable);
        }
      }
    },
    [multiSelect, onSelect, onSelectionChange, variables, disabledIds, selectedIdSet]
  );

  // Handle focus
  const handleFocus = useCallback((id: string) => {
    setFocusedId(id);
  }, []);

  // Expand all categories
  const handleExpandAll = useCallback(() => {
    const allIds = new Set<string>();
    function collectIds(node: TreeNode) {
      if (node.children) {
        allIds.add(node.id);
        node.children.forEach(collectIds);
      }
    }
    tree.forEach(collectIds);
    setExpandedIds(allIds);
  }, [tree]);

  // Collapse all
  const handleCollapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  return (
    <div className={`bg-card rounded-lg overflow-hidden flex flex-col ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base sm:text-lg font-semibold text-foreground">Output Variables</h3>
          <div className="flex gap-1">
            <button
              onClick={handleExpandAll}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
              title="Expand all categories"
              aria-label="Expand all categories"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
            <button
              onClick={handleCollapseAll}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
              title="Collapse all categories"
              aria-label="Collapse all categories"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
              </svg>
            </button>
          </div>
        </div>

        {/* Search filter */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={filter}
            onChange={(e) => { setFilter(e.target.value); }}
            placeholder="Search variables..."
            className="w-full pl-9 pr-3 py-1.5 bg-secondary border border-border rounded text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            aria-label="Search output variables"
          />
          {filter && (
            <button
              onClick={() => setFilter('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="mt-2 text-xs text-muted-foreground flex items-center justify-between">
          <span>
            {filter ? `${visibleNodeIds.filter(id => id.startsWith('var-')).length} of ` : ''}
            {variables.length} variables
          </span>
          {selectedIdSet.size > 0 && (
            <span className="text-blue-400 font-medium">
              {selectedIdSet.size} selected
            </span>
          )}
        </div>

        {/* Warning message */}
        {warningMessage && (
          <div className="mt-2 px-2 py-1.5 bg-amber-500/20 border border-amber-500/40 rounded text-xs text-amber-300 flex items-start gap-2">
            <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {warningMessage}
          </div>
        )}
      </div>

      {/* Tree view */}
      <div
        ref={containerRef}
        role="tree"
        aria-label="Output variables"
        className="flex-1 overflow-auto focus:outline-none"
        style={{ maxHeight: `${String(maxHeight)}px` }}
        tabIndex={0}
        onFocus={() => {
          if (!focusedId && visibleNodeIds.length > 0) {
            setFocusedId(visibleNodeIds[0] ?? null);
          }
        }}
      >
        {filteredTree.length === 0 ? (
          <div className="text-center text-muted-foreground py-8 px-4">
            <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p>{filter ? 'No variables match your search' : 'No variables available'}</p>
            {filter && (
              <button
                onClick={() => setFilter('')}
                className="mt-2 text-blue-400 hover:text-blue-300 text-sm"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          filteredTree.map((node) => (
            <TreeNodeItem
              key={node.id}
              node={node}
              level={0}
              selectedIds={selectedIdSet}
              disabledIds={disabledIds}
              expandedIds={effectiveExpandedIds}
              onToggle={handleToggle}
              onSelect={handleSelect}
              focusedId={focusedId}
              onFocus={handleFocus}
            />
          ))
        )}
      </div>

      {/* Keyboard hint */}
      <div className="px-3 py-2 border-t border-border text-[10px] text-muted-foreground hidden sm:block">
        <span className="opacity-60">Navigate:</span>{' '}
        <kbd className="px-1 bg-muted rounded">↑↓</kbd>{' '}
        <span className="opacity-60">Expand:</span>{' '}
        <kbd className="px-1 bg-muted rounded">→</kbd>{' '}
        <span className="opacity-60">Select:</span>{' '}
        <kbd className="px-1 bg-muted rounded">Enter</kbd>
      </div>
    </div>
  );
}

export default VariableBrowser;
